import React, { useCallback, useLayoutEffect } from 'react';
import { SectionHeader } from '@sweetgreen/garnish';

import {
  CheckoutLayout,
  CheckoutLayoutBlock,
  CheckoutLayoutColumn,
  LoadingAnimation,
  NotFoundView,
  SignedOutView,
} from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import { useNavigateToMainTabs } from '../../navigation';
import type { CheckoutScreenProps } from '../../navigation/AppNavigation.props';
import {
  CheckoutContextProvider,
  useCheckoutContext,
} from './CheckoutProvider';
import {
  CheckoutAddressForm,
  CheckoutBlockVStack,
  CheckoutPaymentBlockCardIcons,
  CheckoutWrapper,
  ConnectedCheckoutExtrasBlock,
  ConnectedCheckoutHeader,
  ConnectedCheckoutOrderBlock,
  ConnectedCheckoutPaymentBlockAvailableCredit,
  ConnectedCheckoutPaymentBlockCreditCardPicker,
  ConnectedCheckoutPaymentBlockPromoCode,
  ConnectedCheckoutTipBlock,
  ConnectedCustomerOptions,
  ConnectedDeliveryOptions,
  ConnectedFutureDateModal,
  ConnectedOutpostOptions,
  ConnectedPickupOptions,
  ConnectedRewardExceptionModal,
} from './components';
import {
  useConnectedCheckoutRenderControl,
  useConnectedCheckoutViewEventEffect,
  useConnectedDeliveryReorder,
  useSyncDeliveryOrderDetails,
} from './hooks';

export const CheckoutScreen = (props: CheckoutScreenProps) => {
  return (
    <CheckoutContextProvider>
      <CheckoutScreenContent {...props} />
    </CheckoutContextProvider>
  );
};

const CheckoutScreenContent = (props: CheckoutScreenProps) => {
  const { navigation } = props;
  const { t } = useLocalizationContext();

  const {
    customer,
    cart,
    cartOrderType,
    orderAddress,
    addressId,
    isCartPickup,
    isCartOutpost,
    isCartDelivery,
    hasAddress,
    setPayload,
  } = useCheckoutContext();

  // ───── Render Control ───────────────────────────────────────────
  const { isLoggedOut, isLoadingCheckout, isCustomerDataReady, isCartValid } =
    useConnectedCheckoutRenderControl();

  // ───── Checkout Title ───────────────────────────────────────────
  const title = t('checkout.finish-order', { cartOrderType });

  useLayoutEffect(() => {
    navigation.setOptions({ title });
  }, [navigation, title]);

  // ───── Effects ──────────────────────────────────────────────────
  useConnectedDeliveryReorder();
  useSyncDeliveryOrderDetails({
    cart,
    addressId,
    setPayload,
  });

  // ───── Telemetry ────────────────────────────────────────────────
  useConnectedCheckoutViewEventEffect({
    isCustomerDataReady,
    isLoadingCheckout,
  });

  // ───── Not Found View ───────────────────────────────────────────
  const navigateToMainTabs = useNavigateToMainTabs(navigation);
  const dismissAndNavigateToLocations = useCallback(() => {
    void navigateToMainTabs('MenuTab', { screen: 'Locations' });
  }, [navigateToMainTabs]);

  // ───── Loading ──────────────────────────────────────────────────
  if (isLoadingCheckout) return <LoadingAnimation />;

  // ───── Logged Out -> Login ──────────────────────────────────────
  if (isLoggedOut) {
    return <SignedOutView />;
  }

  // ───── Render Control ───────────────────────────────────────────
  if (!isCustomerDataReady) return <LoadingAnimation />;

  // ───── Empty Cart -> Location ───────────────────────────────────
  if (!isCartValid) {
    return (
      <NotFoundView
        btnLabel={t('not-found-view.empty-cart')}
        description={t('bag.empty')}
        onBtnPress={dismissAndNavigateToLocations}
      />
    );
  }

  if (isCartDelivery && !hasAddress) {
    return <CheckoutAddressForm orderAddress={orderAddress ?? undefined} />;
  }

  return (
    <CheckoutWrapper>
      {/* Header */}
      <ConnectedCheckoutHeader />

      <CheckoutLayout>
        <CheckoutLayoutColumn>
          {/* Details Block */}
          <CheckoutLayoutBlock>
            <SectionHeader
              heading={t('checkout.block-details-title', { cartOrderType })}
            />
            <CheckoutBlockVStack>
              {isCartPickup ? <ConnectedPickupOptions /> : null}
              {isCartOutpost ? <ConnectedOutpostOptions /> : null}
              {isCartDelivery ? <ConnectedDeliveryOptions /> : null}
              {customer ? <ConnectedCustomerOptions /> : null}
            </CheckoutBlockVStack>
          </CheckoutLayoutBlock>

          {/* Payment Block */}
          <CheckoutLayoutBlock>
            <CheckoutPaymentBlockCardIcons />
            <CheckoutBlockVStack>
              <ConnectedCheckoutPaymentBlockCreditCardPicker />
              <ConnectedCheckoutPaymentBlockAvailableCredit />
              <ConnectedCheckoutPaymentBlockPromoCode />
            </CheckoutBlockVStack>
          </CheckoutLayoutBlock>

          {/* Delivery Block */}
          {isCartDelivery ? (
            <>
              <ConnectedCheckoutTipBlock />
              <ConnectedCheckoutExtrasBlock />
            </>
          ) : null}
        </CheckoutLayoutColumn>

        {/* OrderBlock */}
        <CheckoutLayoutColumn>
          <ConnectedCheckoutOrderBlock />
        </CheckoutLayoutColumn>
      </CheckoutLayout>

      {/* Modals */}
      <ConnectedRewardExceptionModal />
      <ConnectedFutureDateModal />
    </CheckoutWrapper>
  );
};
